import { works } from "@/mocks/works";
import { newsList } from "@/mocks/news";
import { plots } from "@/mocks/plots";

export function getAllWorks() {
  return works;
}

export function getAllNews() {
  return newsList;
}

export function getAllPlots() {
  return plots;
}

export function getWork(id: string) {
  return works.find((w) => w.id === id);
}

export function getNews(id: string) {
  return newsList.find((n) => n.id === id);
}

export function getPlot(id: string) {
  return plots.find((p) => p.id === id);
}

/** Unified detail resolver: works -> w, news -> n, plots -> p */
export function resolveDetail(id: string) {
  if (!id) return { kind: "none" as const };
  const head = id.charAt(0).toLowerCase();
  if (head === "w") {
    const work = getWork(id);
    return work ? { kind: "work" as const, work } : { kind: "none" as const };
  }
  if (head === "n") {
    const news = getNews(id);
    return news ? { kind: "news" as const, news } : { kind: "none" as const };
  }
  if (head === "p") {
    const plot = getPlot(id);
    return plot ? { kind: "plot" as const, plot } : { kind: "none" as const };
  }
  return { kind: "none" as const };
}

export function formatDateCn(date: string) {
  const parts = date.split("-");
  if (parts.length !== 3) return date;
  return `${parts[0]}年${Number(parts[1])}月${Number(parts[2])}日`;
}

export function getRelatedNews(workId: string) {
  return newsList.filter((n) => n.relatedWorkId === workId);
}

export function getRelatedPlots(workId: string) {
  return plots.filter((p) => p.relatedWorkId === workId);
}

export function getSimilarWorks(workId: string, limit = 4) {
  const current = getWork(workId);
  if (!current) return works.slice(0, limit);
  const byGenre = works.filter(
    (w) => w.id !== workId && w.genre.some((g) => current.genre.includes(g)),
  );
  const fill = works.filter((w) => w.id !== workId && !byGenre.includes(w));
  return [...byGenre, ...fill].slice(0, limit);
}

export interface SearchResult {
  works: typeof works;
  news: typeof newsList;
  plots: typeof plots;
}

export function searchContent(keyword: string): SearchResult {
  const q = keyword.trim().toLowerCase();
  if (!q) return { works: [], news: [], plots: [] };
  const match = (text: string) => text.toLowerCase().includes(q);
  return {
    works: works.filter(
      (w) =>
        match(w.title) ||
        match(w.director) ||
        w.cast.some((c) => match(c)) ||
        w.genre.some((g) => match(g)) ||
        w.tags.some((t) => match(t)) ||
        match(w.synopsis),
    ),
    news: newsList.filter(
      (n) => match(n.title) || match(n.summary) || match(n.content) || match(n.type),
    ),
    plots: plots.filter(
      (p) =>
        match(p.title) ||
        match(p.summary) ||
        match(p.workTitle) ||
        p.tags.some((t) => match(t)) ||
        match(p.content),
    ),
  };
}

export function getHotWorks(limit = 5) {
  return [...works].sort((a, b) => b.rating - a.rating).slice(0, limit);
}

export function getLatestNews(limit = 5) {
  return [...newsList].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, limit);
}

export function getLatestPlots(limit = 3) {
  return [...plots].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, limit);
}